import { Component, OnInit, OnDestroy, signal, computed } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Subject, takeUntil, forkJoin } from 'rxjs';

import { User, Chatbot } from '../../core/models/user.model';
import { UserManagementService } from '../../core/services/user-management.service';
import { ChatbotManagementService } from '../../core/services/chatbot-management.service';

import { UserEditModalComponent } from './components/user-edit-modal.component';
import { USER_TABLE_CONFIG } from './config/table.config';
import { DataTableComponent, TableColumn, TableAction, TableConfig } from '../../shared/components/data-table';

import { LucideAngularModule, Pen, Plus, UserRoundX } from "lucide-angular";
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';

@Component({
  selector: 'app-user-management',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatChipsModule,
    MatCheckboxModule,
    MatDialogModule,
    MatSnackBarModule,
    DataTableComponent,
    LucideAngularModule,
    PageHeaderComponent
],
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  
  // State signals
  users = signal<User[]>([]);
  allChatbots = signal<Chatbot[]>([]);
  loading = signal<boolean>(false);
  activeOnly = signal<boolean>(false);
  readonly Pen = Pen;
  readonly Plus = Plus;
  readonly UserRoundX = UserRoundX;
  // Definición de columnas - se inicializa en el constructor
  tableColumns: TableColumn<User>[] = [
    {
      key: 'userId',
      header: 'ID',
      type: 'text',
      width: '80px',
      sortable: true
    },
    {
      key: 'email',
      header: 'Email',
      type: 'text',
      searchable: true,
      sortable: true
    },
    {
      key: 'chatbots',
      header: 'Chatbots Asociados',
      type: 'chips',
      chipsConfig: {
        getItems: (user: User) => user.chatbots,
        labelProperty: 'name',
        emptyMessage: 'Sin chatbots'
      },
      sortable: false
    },
    {
      key: 'isActive',
      header: 'Estado',
      type: 'badge',
      width: '120px',
      badgeConfig: {
        getValue: (user: User) => user.isActive,
        trueLabel: 'Activo',
        falseLabel: 'Inactivo',
        trueClass: 'bg-green-100 text-green-800',
        falseClass: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
      },
      sortable: true
    },
    {
      key: 'updatedAt',
      header: 'Última Actualización',
      type: 'text',
      formatter: (value: any, user: User) => {
        const date = new Date(user.updatedAt).toLocaleDateString('es-CL', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
        const by = user.updatedBy ? ` (Por: ${user.updatedBy})` : '';
        return date + by;
      },
      sortable: true
    }
  ];
  
  // Definición de acciones
  tableConfig: TableConfig = {
    searchable: true,
    searchPlaceholder: 'Ingrese email o nombre de chatbot',
    pageable: true,
    defaultPageSize: USER_TABLE_CONFIG.defaultPageSize,
    pageSizeOptions: USER_TABLE_CONFIG.pageSizeOptions,
    showFirstLastButtons: USER_TABLE_CONFIG.showFirstLastButtons,
    emptyMessage: 'No se encontraron usuarios',
    emptyLucideIcon: this.UserRoundX,
    loadingMessage: 'Cargando usuarios...',
    resultsSingular: 'usuario',
    resultsPlural: 'usuarios'
  };
  
  // Definición de acciones
  tableActions: TableAction<User>[] = [
    {
      icon: 'edit',
      color: 'primary',
      tooltip: 'Editar usuario',
      callback: (user) => this.openEditModal(user),
      lucideIcon: this.Pen
    }
  ];
  
  // Función personalizada de búsqueda
  searchFunction = (user: User, term: string): boolean => {
    const lowerTerm = term.toLowerCase();
    const emailMatch = user.email.toLowerCase().includes(lowerTerm);
    const chatbotMatch = user.chatbots.some(chatbot =>
      chatbot.name.toLowerCase().includes(lowerTerm) ||
      chatbot.code.toLowerCase().includes(lowerTerm)
    );
    return emailMatch || chatbotMatch;
  };
  
  // Función personalizada de filtrado (para "Solo activos")
  filterFunction = (): boolean => {
    return true; // El filtro se aplicará con computed
  };
  
  // Computed para filtrar usuarios activos
  filteredUsers = computed(() => {
    const users = this.users();
    const onlyActive = this.activeOnly();
    
    if (onlyActive) {
      return users.filter(user => user.isActive);
    }
    
    return users;
  });

  constructor(
    private readonly userService: UserManagementService,
    private readonly chatbotService: ChatbotManagementService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.loadData();
    
    // Listen to query params to open modals
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const action = params['action'];
        
        if (action === 'new') {
          this.openCreateModalFromRoute();
        } else if (action === 'edit') {
          const userId = params['userId'];
          if (userId) {
            this.openEditModalFromRoute(+userId);
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load users and chatbots in parallel
   */
  loadData(): void {
    this.loading.set(true);
    
    forkJoin({
      users: this.userService.getUsers(),
      chatbots: this.chatbotService.getAllChatbots()
    })
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (result) => {
        this.users.set(result.users);
        this.allChatbots.set(result.chatbots);
        this.loading.set(false);
      },
      error: (error) => {
        this.loading.set(false);
        this.showError('Error al cargar los datos');
      }
    });
  }

  /**
   * Load all users from API
   */
  loadUsers(): void {
    this.loading.set(true);
    
    this.userService.getUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (users) => {
          this.users.set(users);
          this.loading.set(false);
        },
        error: (error) => {
          this.loading.set(false);
          this.showError('Error al cargar los usuarios');
        }
      });
  }

  /**
   * Open edit modal for a user
   */
  openEditModal(user: User): void {
    // Navigate using query params to avoid component recreation
    this.router.navigate([], { 
      relativeTo: this.route,
      queryParams: { action: 'edit', userId: user.userId },
      queryParamsHandling: 'merge'
    });
  }

  /**
   * Open modal to create a new user
   */
  openCreateModal(): void {
    // Navigate using query params to avoid component recreation
    this.router.navigate([], { 
      relativeTo: this.route,
      queryParams: { action: 'new' },
      queryParamsHandling: 'merge'
    });
  }

  /**
   * Open create modal from route - called when navigating to /user-management?action=new
   */
  private openCreateModalFromRoute(): void {
    // Wait for data to load before opening modal
    if (this.loading()) {
      setTimeout(() => this.openCreateModalFromRoute(), 100);
      return;
    }

    const dialogRef = this.dialog.open(UserEditModalComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      panelClass: 'custom-dialog-container',
      data: { 
        user: null,
        allChatbots: this.allChatbots() // Pasar los chatbots precargados
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        // Clear query params to return to base route
        this.router.navigate([], { 
          relativeTo: this.route,
          queryParams: {}
        });
        
        if (result) {
          this.loadUsers();
          this.showSuccess('Usuario creado correctamente');
        }
      });
  }

  /**
   * Open edit modal from route - called when navigating to /user-management?action=edit&userId=X
   */
  private openEditModalFromRoute(userId: number): void {
    // Wait for data to load before opening modal
    if (this.loading()) {
      setTimeout(() => this.openEditModalFromRoute(userId), 100);
      return;
    }

    // Find user by ID
    const user = this.users().find(u => u.userId === userId);
    
    if (!user) {
      this.showError('Usuario no encontrado');
      // Clear query params
      this.router.navigate([], { 
        relativeTo: this.route,
        queryParams: {}
      });
      return;
    }

    const dialogRef = this.dialog.open(UserEditModalComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      panelClass: 'custom-dialog-container',
      data: { 
        user: { ...user },
        allChatbots: this.allChatbots() // Pasar los chatbots precargados
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        // Clear query params to return to base route
        this.router.navigate([], { 
          relativeTo: this.route,
          queryParams: {}
        });
        
        if (result) {
          // Reload users after successful update
          this.loadUsers();
          this.showSuccess('Usuario actualizado correctamente');
        }
      });
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Show success message
   */
  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 3000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['success-snackbar']
    });
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 5000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['error-snackbar']
    });
  }
}

